import { useEffect, useMemo, useState } from 'react';
import { ChevronDown, MessageCircle, Send, X } from 'lucide-react';
import { useCompanyCountry } from '@/contexts/WorkspaceContext';
import { findCountryByCode } from '@/data/countries';
import { whatsappAccountService, WhatsappAccount } from '@/services/whatsapp-account.service';
import type { SelectOption } from '@/components/SearchableSelect';
import type { AiAgentWebchat } from '@/services/ai-agents.service';

/** WhatsApp numbers an agent can borrow its AI setup from. Numbers in AI chat mode come first. */
export function useBrainOptions() {
  const [accounts, setAccounts] = useState<WhatsappAccount[] | null>(null);
  useEffect(() => {
    // A workspace without the WhatsApp modules answers 403 — that just means "no numbers".
    whatsappAccountService.list().then(setAccounts).catch(() => setAccounts([]));
  }, []);
  const options = useMemo<SelectOption[]>(() => {
    const rows = [...(accounts ?? [])].sort((a, b) => Number(b.botMode === 'ai_chat') - Number(a.botMode === 'ai_chat'));
    return [
      ...rows.map((a) => ({
        value: a.accountUid,
        label: `${a.businessName} · ${a.displayPhoneNumber}`,
        description: a.botMode === 'ai_chat' ? 'WhatsApp AI chat is on — same answers on every channel' : 'Uses this number’s AI settings (its WhatsApp bot mode doesn’t matter here)',
      })),
      { value: '', label: 'Its own persona', description: 'Set it up below — business profile, catalog and knowledge apply either way' },
    ];
  }, [accounts]);
  const firstAiAccount = accounts?.find((a) => a.botMode === 'ai_chat')?.accountUid ?? accounts?.[0]?.accountUid;
  return { options, loading: accounts === null, firstAiAccount };
}

/** TabbedPage renders tabs bare — sub-pages bring their own page padding. */
export function TabBody({ children }: { children: React.ReactNode }) {
  return <div className="w-full px-4 py-6 sm:px-7">{children}</div>;
}

export const COLOR_PRESETS = ['#B8913A', '#0F766E', '#2563EB', '#7C3AED', '#DB2777', '#DC2626', '#EA580C', '#18181B'];

/**
 * A faithful mock of the visitor's chat (same layout and colours as the real
 * frame) that follows unsaved settings live. The real thing is one click away
 * via "Try it".
 */
export function WidgetPreview({ cfg, title }: { cfg: Pick<AiAgentWebchat, 'color' | 'position' | 'preChat' | 'greeting' | 'logoUrl'>; title: string }) {
  const [view, setView] = useState<'form' | 'chat'>(cfg.preChat === 'none' ? 'chat' : 'form');
  useEffect(() => { setView(cfg.preChat === 'none' ? 'chat' : 'form'); }, [cfg.preChat]);
  const left = cfg.position === 'left';
  const greeting = cfg.greeting?.trim() || 'Hi! How can we help you today?';
  const phone = cfg.preChat === 'name_phone' || cfg.preChat === 'name_phone_or_email';
  const email = cfg.preChat === 'name_email' || cfg.preChat === 'name_phone_or_email';
  const country = findCountryByCode(useCompanyCountry());

  return (
    <div className="relative h-[540px] overflow-hidden rounded-2xl border border-border bg-[repeating-linear-gradient(45deg,var(--color-surface-alt),var(--color-surface-alt)_10px,var(--color-surface)_10px,var(--color-surface)_20px)]">
      <div className="absolute left-3 top-3 rounded-md bg-surface/90 px-2 py-1 text-[10px] font-medium uppercase tracking-wide text-text-muted">Your website</div>
      <div className={`absolute bottom-[78px] flex h-[400px] w-[290px] max-w-[calc(100%-24px)] flex-col overflow-hidden rounded-2xl bg-white text-[12.5px] text-zinc-900 shadow-2xl ${left ? 'left-3' : 'right-3'}`}>
        <div className="flex items-center gap-2 px-3 py-2.5 text-white" style={{ background: cfg.color }}>
          <span className="flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-full bg-white/25 text-xs font-bold">
            {cfg.logoUrl ? <img src={cfg.logoUrl} alt="" className="h-full w-full object-cover" /> : (title.trim()[0] ?? '?').toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <div className="truncate text-[13px] font-semibold">{title || 'Chat'}</div>
            <div className="text-[10px] opacity-85">AI assistant · our team can join anytime</div>
          </div>
          <X size={14} />
        </div>
        {view === 'form' ? (
          <div className="flex flex-1 flex-col gap-2 p-3">
            <p className="text-zinc-500">Tell us how to reach you, then ask us anything.</p>
            <MockField label="Name" />
            {phone && (
              <div>
                <div className="mb-0.5 text-[10.5px] font-semibold text-zinc-600">Phone</div>
                <div className="flex gap-1.5">
                  <div className="flex h-7 items-center gap-1 rounded-lg border border-zinc-200 px-2 text-[11px]">{country?.flag} {country?.dial}<ChevronDown size={10} /></div>
                  <div className="h-7 flex-1 rounded-lg border border-zinc-200" />
                </div>
              </div>
            )}
            {email && <MockField label="Email" />}
            <button type="button" onClick={() => setView('chat')} className="mt-1 rounded-lg py-2 text-center font-semibold text-white" style={{ background: cfg.color }}>Start chat</button>
          </div>
        ) : (
          <div className="flex flex-1 flex-col gap-2 bg-zinc-50 p-3">
            <Bubble>{greeting}</Bubble>
            <Bubble mine color={cfg.color}>Do you deliver to Dubai Marina?</Bubble>
            <Bubble>Yes — we deliver across Dubai. What would you like to order?</Bubble>
          </div>
        )}
        <div className="flex items-center gap-2 border-t border-zinc-200 p-2">
          <div className="flex-1 rounded-lg border border-zinc-200 px-2 py-1.5 text-zinc-400">Type your message…</div>
          <span className="flex h-7 w-7 items-center justify-center rounded-lg text-white" style={{ background: cfg.color }}><Send size={12} /></span>
        </div>
      </div>
      <span className={`absolute bottom-3 flex h-12 w-12 items-center justify-center rounded-full text-white shadow-xl ${left ? 'left-3' : 'right-3'}`} style={{ background: cfg.color }}>
        <MessageCircle size={20} />
      </span>
    </div>
  );
}

function MockField({ label }: { label: string }) {
  return (
    <div>
      <div className="mb-0.5 text-[10.5px] font-semibold text-zinc-600">{label}</div>
      <div className="h-7 rounded-lg border border-zinc-200" />
    </div>
  );
}

function Bubble({ children, mine, color }: { children: React.ReactNode; mine?: boolean; color?: string }) {
  return (
    <div
      className={`max-w-[85%] rounded-xl px-2.5 py-1.5 ${mine ? 'self-end rounded-br-sm text-white' : 'self-start rounded-bl-sm border border-zinc-200 bg-white'}`}
      style={mine ? { background: color } : undefined}
    >
      {children}
    </div>
  );
}
